import * as api from './api'
import { AppDispatch, AppState } from 'src/app/store'
import * as reducers from './slice-reducers'
import { extractFormData, getFileFromBlobUrl } from 'src/core/helpers'
import {
  CreditCardOrders, PrePurchaseBuyNow,
  IntegrationPurchase, IntegrationStatics,
  MyIntegrationsType, MainIntegrationChart,
  OffsetOptionalFieldsType, SmallWhiteLabel,
  IntegrationProjects, SmallProjectIntegration,
  DeveloperChartView,
} from 'src/core/models'


export const createIntegration = (payload: any, currency_code: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setCreateIntegrationLoading(true))
    const data: any = await api.createIntegrationAPI(payload)
    if (data?.OperationResult === 'FAILED' && data?.errors?.length > 0) {
      dispatch(reducers.setCreateIntegrationError({ errors: data?.errors }))
    } else {
      await api.recalculateIntegrationPurchasesAPI(data?.user_purchases_item?.initials)
      dispatch(getUserIntegrationList(currency_code))
      dispatch(getMainIntegrationChartData(currency_code))
      dispatch(reducers.setCreateIntegrationSuccess(true))
    }
    dispatch(reducers.setCreateIntegrationLoading(false))
  } catch (error) {
    dispatch(reducers.setCreateIntegrationLoading(false))
    dispatch(reducers.setCreateIntegrationSuccess(false))
  }
}

export const getUserIntegrationList = (currency_code: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setUserIntegrationListLoading(true))
    const data: MyIntegrationsType[] = await api.getUserIntegrationsListAPI(currency_code)
    dispatch(reducers.setUserIntegrationListLoading(false))
    dispatch(reducers.setUserIntegrationListData(data))
    dispatch(reducers.setUserIntegrationsFiltered(data))
  } catch (error) {
    dispatch(reducers.setCreateIntegrationLoading(false))
    dispatch(reducers.setCreateIntegrationError(error))
  }
}

export const getMainIntegrationChartData = (
  currency_code: string, chartView: DeveloperChartView = 'AMOUNT'
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setMainChartDataLoading(true))
    const data: MainIntegrationChart = await api.getMainIntegrationChartDataAPI(currency_code, chartView)
    dispatch(reducers.setMainChartDataLoading(false))
    dispatch(reducers.setMainChartData(data))
  } catch (error) {
    dispatch(reducers.setMainChartDataLoading(false))
  }
}

export const getIntegrationPurchasesList = (id: number, currency_code: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setIntegrationPurchasesListLoading(true))
    const data: IntegrationPurchase[] = await api.getIntegrationPurchasesListAPI(id, currency_code)
    dispatch(reducers.setIntegrationPurchasesListLoading(false))
    dispatch(reducers.setIntegrationPurchasesList(data))
    dispatch(reducers.setIntegrationPurchasesFiltered(data))
  } catch (error) {
    dispatch(reducers.setIntegrationPurchasesListLoading(false))
    dispatch(reducers.setIntegrationPurchasesListError(error))
  }
}

export const getIntegrationProjectsList = (id: number, currency_code: string = 'EUR') => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setIntegrationProjectsListLoading(true))
    const data: IntegrationProjects[] = await api.getIntegrationProjectsListAPI(id, currency_code)
    dispatch(reducers.setIntegrationProjectsListLoading(false))
    dispatch(reducers.setIntegrationProjectsList(data))
  } catch (error) {
    dispatch(reducers.setIntegrationPurchasesListLoading(false))
    dispatch(reducers.setIntegrationProjectsListError(error))
  }
}

export const getIntegrationChartData = (
  id: number, currency_code: string, chartView: DeveloperChartView = 'AMOUNT'
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setIntegrationChartLoading(true))
    const data: MainIntegrationChart = await api.getIntegrationChartDataAPI(id, currency_code, chartView)
    dispatch(reducers.setIntegrationChartLoading(false))
    dispatch(reducers.setIntegrationChartData(data))
  } catch (error) {
    dispatch(reducers.setIntegrationChartLoading(false))
  }
}

export const getIntegrationStatics = (id: number, currency_code: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setIntegrationStaticsLoading(true))
    const data: IntegrationStatics = await api.getIntegrationStaticsAPI(id, currency_code)
    dispatch(reducers.setIntegrationStaticsLoading(false))
    dispatch(reducers.setIntegrationStaticsData(data))
  } catch (error) {
    dispatch(reducers.setIntegrationStaticsLoading(false))
  }
}

export const getAllUserIntegrationsToShareForAccount = (integration_id: number, project_id: number) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(reducers.setIntegrationToShareProjectsListLoading(true))
      const data: MyIntegrationsType[] = await api.getAllUserIntegrationsToShareForAccount(integration_id, project_id)
      dispatch(reducers.setIntegrationToShareProjectsListLoading(false))
      dispatch(reducers.setIntegrationToShareProjectsListProjectId(project_id))
      dispatch(reducers.setIntegrationToShareProjectsListData(data))
      dispatch(reducers.setIntegrationToShareProjectsListFlag(false))
    } catch (error) {
      dispatch(reducers.setIntegrationToShareProjectsListLoading(false))
      dispatch(reducers.setIntegrationToShareProjectsListError(error))
    }
  }
export const cleanProjectId = () => async (dispatch: AppDispatch) => {
  dispatch(reducers.setIntegrationToShareProjectsListProjectId(''))
}

export const addProjectToIntegrations = (project_id: number, integration_code: any, integration_id: any) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(reducers.setIntegrationToShareProjectsListLoading(true))
      await api.addProjectToIntegrations(project_id, integration_code)
      dispatch(reducers.setIntegrationToShareProjectsListLoading(false))
      dispatch(reducers.setIntegrationToShareProjectsListProjectId(project_id))
      dispatch(getAllUserIntegrationsToShareForAccount(integration_id, project_id))
      dispatch(reducers.setIntegrationToShareProjectsListFlag(true))
    } catch (error) {
      dispatch(reducers.setIntegrationToShareProjectsListLoading(false))
      dispatch(reducers.setIntegrationToShareProjectsListFlag(false))
      dispatch(reducers.setIntegrationToShareProjectsListError(error))
    }
  }

export const deactivateIntegration = (
  integration_id: number, currency_code: string
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setDeactivateIntegrationLoading(true))
    await api.deactivateIntegration(integration_id, false)
    dispatch(reducers.setDeactivateIntegrationLoading(false))
    dispatch(getUserIntegrationList(currency_code))
    dispatch(getMainIntegrationChartData(currency_code))
  } catch (error) {
    dispatch(reducers.setDeactivateIntegrationLoading(false))
    dispatch(reducers.setDeactivateIntegrationError(error))
  }
}


export const reactivateIntegration = (
  integration_code: string,
  currency_code: string
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setReactivateIntegrationLoading(true))
    await api.reactivateIntegrationAPI(integration_code)
    dispatch(reducers.setReactivateIntegrationLoading(false))
    dispatch(reducers.setReactivateIntegrationSuccess(true))
    dispatch(getUserIntegrationList(currency_code))
    dispatch(getMainIntegrationChartData(currency_code))
  } catch (error) {
    dispatch(reducers.setReactivateIntegrationLoading(false))
    dispatch(reducers.setReactivateIntegrationSuccess(false))
    dispatch(reducers.setReactivateIntegrationError(error))
  }
}

export const updateIntegrationLogo = (
  id: number, logo: string, currency_code: string
) => async (dispatch: AppDispatch) => {
  try {
    const filesData = { logo: logo ?  await getFileFromBlobUrl(logo) : '' }
    const payload = extractFormData(filesData)
    dispatch(reducers.setUpdateLogoLoading(true))
    await api.updateLogoAPI(id, payload)
    dispatch(reducers.setUpdateLogoLoading(false))
    dispatch(reducers.setUpdateLogoSuccess(true))
    dispatch(getIntegrationStatics(id, currency_code))
  } catch (error) {
    dispatch(reducers.setUpdateLogoLoading(false))
    dispatch(reducers.setUpdateLogoSuccess(false))
  }
}


export const preparePurchasesForOffset = (
  excelData: any[], identifierField: string,
  integrationToken: string, co2AmountField: string
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setPreparePurchasesLoading(true))
    const arrayContract = excelData.map(p => p[identifierField])
    const responseData = await api.validateOffsetFromFileAPI(arrayContract, integrationToken)
    const repeatedPurchases = responseData.transactions.map((t: any) => t.transaction_identifier)
    const filteredPurchases = excelData.filter(p => !repeatedPurchases.includes(p[identifierField]))
    const finalPurchases = filteredPurchases?.map((p: any) => {
      return { ...p, co2AmountField: p[co2AmountField] }
    })
    dispatch(reducers.setPreparePurchasesData(finalPurchases))
    dispatch(reducers.setPreparePurchasesLoading(false))
  } catch (error) {
    dispatch(reducers.setPreparePurchasesLoading(false))
  }
}

export const makeOffsetPurchases = (requestData: any[], integrationToken: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setOffsetPurchasesLoading(true))
    for (const body of requestData) {
      try {
        const response = await api.offsetPurchaseAPI(body, integrationToken)
        if (response.status === 201) {
          dispatch(reducers.setOffsetPurchasesPurchasesResults(response?.data?.offsets[0]))
        }
      } catch (e) {
        const errors = []
        for (const property in e?.response?.data) {
          errors.push(`${property}: ${e?.response?.data[property]}`)
        }
        const idf = body?.offsets[0]?.extra_data?.transaction_identifier
        const purchaseError = { transaction_identifier: idf, error: errors[0] }
        dispatch(reducers.setOffsetPurchasesFailedResults(purchaseError))
      }
    }
    dispatch(reducers.setOffsetPurchasesHasFinished(true))
  } catch (error) {
    dispatch(reducers.setOffsetPurchasesLoading(false))
    dispatch(reducers.setOffsetPurchasesHasFinished(true))
  }
}

export const getAllIntegrationProjects = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setAllProjectsIntegrationLoading(true))
    const data: SmallProjectIntegration[] = await api.getProjectsIntegrationsAPI()
    dispatch(reducers.setAllProjectsIntegrationLoading(false))
    dispatch(reducers.setAllProjectsIntegrationData(data))
  } catch (error) {
    dispatch(reducers.setAllProjectsIntegrationLoading(false))
  }
}

export const getCreditCardOrders = (orderId: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setCreateDepositLoading(true))
    const data: CreditCardOrders = await api.getCreditCardOrdersAPI(orderId)
    dispatch(reducers.setCreateDepositLoading(false))
    dispatch(reducers.setCreateDepositData(data))
  } catch (error) {
    dispatch(reducers.setCreateDepositLoading(false))
    dispatch(reducers.setCreateDepositError(error))
  }
}

export const prePurchaseBuyNow = (
  integration_code: string | string[],
  payment_method: string,
  pre_purchased: any,
  deposit: any) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(reducers.setPrePurchaseLoading(true))
      const data: PrePurchaseBuyNow = await api.prePurchaseBuyNowAPI(
        integration_code, payment_method, pre_purchased, deposit)
      dispatch(reducers.setPrePurchaseLoading(false))
      dispatch(reducers.setPrePurchaseOrderId(data.order))
      dispatch(reducers.setPrePurchaseSuccess(true))
    } catch (error) {
      dispatch(reducers.setPrePurchaseLoading(false))
      dispatch(reducers.setPrePurchaseError(error))
    }
  }

export const getIntegrationOptionalFields = (integrationId: number) => async (dispatch: AppDispatch) => {
  try {
    const fields: OffsetOptionalFieldsType = await api.getIntegrationOptionalFieldsAPI(integrationId)
    dispatch(reducers.setOffsetOptionalFields(fields))
  } catch (error) {
    console.warn('Error on Integration optional fields', error)
  }
}

export const getIntegrationWhiteLabelDetails = (integrationId: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setWhiteLabelDetailsLoading(true))
    const whitelabel: SmallWhiteLabel = await api.getIntegrationWhiteLabelAPI(integrationId)
    dispatch(reducers.setWhiteLabelDetailsData(whitelabel))
    dispatch(reducers.setWhiteLabelDetailsLoading(false))
  } catch (error) {
    console.warn('Error on Integration White Label', error)
    dispatch(reducers.setWhiteLabelDetailsLoading(false))
  }
}

export const updateIntegrationWhiteLabel = (
  integrationId: number, payload: object
) => async () => {
  try {
    await api.updateIntegrationWhiteLabelAPI(integrationId, payload)
  } catch (error) {
    console.warn('Error on Update White Label', error)
  }
}

export const recalculateIntegration = (initials: string) => async () => {
  try {
    await api.recalculateIntegrationPurchasesAPI(initials)
  } catch (error) {
    console.warn('Error on Recalculate Integration', error)
  }
}
export const getFinalPrice = (amount = 1) => async (dispatch:AppDispatch,getState:()=>AppState) => {
  const { users: { user: { country } },
    myIntegration: { offsetByExcel: {
      preparePurchases: { selectedProjectId: project_id },
    },
    integrationStatics: { data: { integration_initials: initials } }
    } } = getState()
  const params = { amount, project_id, initials, country, source: 'market' }
  const finalPrice = await api.fetchFinalPrice(params)
  dispatch(reducers.setFinalUnitPrice(finalPrice))
  return finalPrice
}
